import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { getSelectionsForSource, getWeaponMasteryEntitlements } from '../utils/weaponMastery';

/**
 * New Weapon Mastery weapons a level grants (Barbarian 4 and 10, Fighter 4/10/16, and so on), chosen in the level-up
 * window. Keeps the weapons already mastered and asks only for the new ones; "Decide later" leaves them for Character
 * Features. Each change reports the full selection list (or null until every new pick is made) through onChange.
 */
export default function LevelUpMasteryChoice({ form, nextForm, sourceId, onChange }) {
  const [catalog, setCatalog] = useState({});
  const [picks, setPicks] = useState([]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await apiFetch('/weapons');
        const data = response?.ok ? await response.json() : {};
        if (live) setCatalog(data || {});
      } catch { if (live) setCatalog({}); }
    })();
    return () => { live = false; };
  }, []);
  const current = useMemo(() => getSelectionsForSource(form, sourceId), [form, sourceId]);
  const entitlement = useMemo(() => getWeaponMasteryEntitlements(nextForm, catalog).find((entry) => entry.sourceId === sourceId), [catalog, nextForm, sourceId]);
  const extra = Math.max(0, (entitlement?.count || 0) - current.length);
  const options = Object.entries(catalog).map(([key, weapon]) => ({ key: String(key).toLowerCase(), name: weapon?.name || key }))
    .filter(({ key }) => entitlement?.eligibleWeaponKeys.includes(key) && !current.includes(key)).sort((a, b) => a.name.localeCompare(b.name));
  const pick = (index, key) => {
    const next = [...picks]; next[index] = key;
    setPicks(next);
    const chosen = next.slice(0, extra).filter(Boolean);
    onChange?.(extra > 0 && chosen.length === extra ? [...current, ...chosen] : null);
  };
  if (!extra) return null;
  return (
    <div className="levelup-choice" role="group" aria-label="Weapon Mastery">
      <span className="form-label">Weapon Mastery</span>
      <p className="levelup-choice__hint">
        {`You can master ${extra} more weapon${extra === 1 ? '' : 's'}.`}
        {current.length ? ` Already mastered: ${current.map((key) => catalog[key]?.name || key).join(', ')}.` : ''} Or decide later in Character Features.
      </p>
      {Array.from({ length: extra }, (_, index) => (
        <Form.Group className="mb-2" controlId={`levelup-mastery-${index}`} key={index}>
          <Form.Label>{`New mastery ${index + 1}`}</Form.Label>
          <Form.Select value={picks[index] || ''} onChange={(event) => pick(index, event.target.value)}>
            <option value="">Decide later</option>
            {options.map(({ key, name }) => <option key={key} value={key} disabled={picks.includes(key) && picks[index] !== key}>{name}</option>)}
          </Form.Select>
        </Form.Group>
      ))}
    </div>
  );
}
